import type { PresetDefinition, PresetFamilyDefinition } from '@/types/palette';

const swatch = (step: number, hex: string): { step: number; hex: string } => ({ step, hex });

/** Tailwind CSS v4 default color palette (OKLCH, 11 steps). */
const TAILWIND: PresetFamilyDefinition[] = [
  {
    id: 'red',
    name: 'Red',
    steps: [
      swatch(50, '#FEF2F2'),
      swatch(100, '#FEE2E2'),
      swatch(200, '#FECACA'),
      swatch(300, '#FCA5A5'),
      swatch(400, '#F87171'),
      swatch(500, '#EF4444'),
      swatch(600, '#DC2626'),
      swatch(700, '#B91C1C'),
      swatch(800, '#991B1B'),
      swatch(900, '#7F1D1D'),
      swatch(950, '#450A0A'),
    ],
  },
  {
    id: 'orange',
    name: 'Orange',
    steps: [
      swatch(50, '#FFF7ED'),
      swatch(100, '#FFEDD5'),
      swatch(200, '#FED7AA'),
      swatch(300, '#FDBA74'),
      swatch(400, '#FB923C'),
      swatch(500, '#F97316'),
      swatch(600, '#EA580C'),
      swatch(700, '#C2410C'),
      swatch(800, '#9A3412'),
      swatch(900, '#7C2D12'),
      swatch(950, '#431407'),
    ],
  },
  {
    id: 'amber',
    name: 'Amber',
    steps: [
      swatch(50, '#FFFBEB'),
      swatch(100, '#FEF3C7'),
      swatch(200, '#FDE68A'),
      swatch(300, '#FCD34D'),
      swatch(400, '#FBBF24'),
      swatch(500, '#F59E0B'),
      swatch(600, '#D97706'),
      swatch(700, '#B45309'),
      swatch(800, '#92400E'),
      swatch(900, '#78350F'),
      swatch(950, '#451A03'),
    ],
  },
  {
    id: 'yellow',
    name: 'Yellow',
    steps: [
      swatch(50, '#FEFCE8'),
      swatch(100, '#FEF9C3'),
      swatch(200, '#FEF08A'),
      swatch(300, '#FDE047'),
      swatch(400, '#FACC15'),
      swatch(500, '#EAB308'),
      swatch(600, '#CA8A04'),
      swatch(700, '#A16207'),
      swatch(800, '#854D0E'),
      swatch(900, '#713F12'),
      swatch(950, '#422006'),
    ],
  },
  {
    id: 'lime',
    name: 'Lime',
    steps: [
      swatch(50, '#F7FEE7'),
      swatch(100, '#ECFCCB'),
      swatch(200, '#D9F99D'),
      swatch(300, '#BEF264'),
      swatch(400, '#A3E635'),
      swatch(500, '#84CC16'),
      swatch(600, '#65A30D'),
      swatch(700, '#4D7C0F'),
      swatch(800, '#3F6212'),
      swatch(900, '#365314'),
      swatch(950, '#1A2E05'),
    ],
  },
  {
    id: 'green',
    name: 'Green',
    steps: [
      swatch(50, '#F0FDF4'),
      swatch(100, '#DCFCE7'),
      swatch(200, '#BBF7D0'),
      swatch(300, '#86EFAC'),
      swatch(400, '#4ADE80'),
      swatch(500, '#22C55E'),
      swatch(600, '#16A34A'),
      swatch(700, '#15803D'),
      swatch(800, '#166534'),
      swatch(900, '#14532D'),
      swatch(950, '#052E16'),
    ],
  },
  {
    id: 'emerald',
    name: 'Emerald',
    steps: [
      swatch(50, '#ECFDF5'),
      swatch(100, '#D1FAE5'),
      swatch(200, '#A7F3D0'),
      swatch(300, '#6EE7B7'),
      swatch(400, '#34D399'),
      swatch(500, '#10B981'),
      swatch(600, '#059669'),
      swatch(700, '#047857'),
      swatch(800, '#065F46'),
      swatch(900, '#064E3B'),
      swatch(950, '#022C22'),
    ],
  },
  {
    id: 'teal',
    name: 'Teal',
    steps: [
      swatch(50, '#F0FDFA'),
      swatch(100, '#CCFBF1'),
      swatch(200, '#99F6E4'),
      swatch(300, '#5EEAD4'),
      swatch(400, '#2DD4BF'),
      swatch(500, '#14B8A6'),
      swatch(600, '#0D9488'),
      swatch(700, '#0F766E'),
      swatch(800, '#115E59'),
      swatch(900, '#134E4A'),
      swatch(950, '#042F2E'),
    ],
  },
  {
    id: 'cyan',
    name: 'Cyan',
    steps: [
      swatch(50, '#ECFEFF'),
      swatch(100, '#CFFAFE'),
      swatch(200, '#A5F3FC'),
      swatch(300, '#67E8F9'),
      swatch(400, '#22D3EE'),
      swatch(500, '#06B6D4'),
      swatch(600, '#0891B2'),
      swatch(700, '#0E7490'),
      swatch(800, '#155E75'),
      swatch(900, '#164E63'),
      swatch(950, '#083344'),
    ],
  },
  {
    id: 'sky',
    name: 'Sky',
    steps: [
      swatch(50, '#F0F9FF'),
      swatch(100, '#E0F2FE'),
      swatch(200, '#BAE6FD'),
      swatch(300, '#7DD3FC'),
      swatch(400, '#38BDF8'),
      swatch(500, '#0EA5E9'),
      swatch(600, '#0284C7'),
      swatch(700, '#0369A1'),
      swatch(800, '#075985'),
      swatch(900, '#0C4A6E'),
      swatch(950, '#082F49'),
    ],
  },
  {
    id: 'blue',
    name: 'Blue',
    steps: [
      swatch(50, '#EFF6FF'),
      swatch(100, '#DBEAFE'),
      swatch(200, '#BFDBFE'),
      swatch(300, '#93C5FD'),
      swatch(400, '#60A5FA'),
      swatch(500, '#3B82F6'),
      swatch(600, '#2563EB'),
      swatch(700, '#1D4ED8'),
      swatch(800, '#1E40AF'),
      swatch(900, '#1E3A8A'),
      swatch(950, '#172554'),
    ],
  },
  {
    id: 'indigo',
    name: 'Indigo',
    steps: [
      swatch(50, '#EEF2FF'),
      swatch(100, '#E0E7FF'),
      swatch(200, '#C7D2FE'),
      swatch(300, '#A5B4FC'),
      swatch(400, '#818CF8'),
      swatch(500, '#6366F1'),
      swatch(600, '#4F46E5'),
      swatch(700, '#4338CA'),
      swatch(800, '#3730A3'),
      swatch(900, '#312E81'),
      swatch(950, '#1E1B4B'),
    ],
  },
  {
    id: 'violet',
    name: 'Violet',
    steps: [
      swatch(50, '#F5F3FF'),
      swatch(100, '#EDE9FE'),
      swatch(200, '#DDD6FE'),
      swatch(300, '#C4B5FD'),
      swatch(400, '#A78BFA'),
      swatch(500, '#8B5CF6'),
      swatch(600, '#7C3AED'),
      swatch(700, '#6D28D9'),
      swatch(800, '#5B21B6'),
      swatch(900, '#4C1D95'),
      swatch(950, '#2E1065'),
    ],
  },
  {
    id: 'purple',
    name: 'Purple',
    steps: [
      swatch(50, '#FAF5FF'),
      swatch(100, '#F3E8FF'),
      swatch(200, '#E9D5FF'),
      swatch(300, '#D8B4FE'),
      swatch(400, '#C084FC'),
      swatch(500, '#A855F7'),
      swatch(600, '#9333EA'),
      swatch(700, '#7E22CE'),
      swatch(800, '#6B21A8'),
      swatch(900, '#581C87'),
      swatch(950, '#3B0764'),
    ],
  },
  {
    id: 'fuchsia',
    name: 'Fuchsia',
    steps: [
      swatch(50, '#FDF4FF'),
      swatch(100, '#FAE8FF'),
      swatch(200, '#F5D0FE'),
      swatch(300, '#F0ABFC'),
      swatch(400, '#E879F9'),
      swatch(500, '#D946EF'),
      swatch(600, '#C026D3'),
      swatch(700, '#A21CAF'),
      swatch(800, '#86198F'),
      swatch(900, '#701A75'),
      swatch(950, '#4A044E'),
    ],
  },
  {
    id: 'pink',
    name: 'Pink',
    steps: [
      swatch(50, '#FDF2F8'),
      swatch(100, '#FCE7F3'),
      swatch(200, '#FBCFE8'),
      swatch(300, '#F9A8D4'),
      swatch(400, '#F472B6'),
      swatch(500, '#EC4899'),
      swatch(600, '#DB2777'),
      swatch(700, '#BE185D'),
      swatch(800, '#9D174D'),
      swatch(900, '#831843'),
      swatch(950, '#500724'),
    ],
  },
  {
    id: 'rose',
    name: 'Rose',
    steps: [
      swatch(50, '#FFF1F2'),
      swatch(100, '#FFE4E6'),
      swatch(200, '#FECDD3'),
      swatch(300, '#FDA4AF'),
      swatch(400, '#FB7185'),
      swatch(500, '#F43F5E'),
      swatch(600, '#E11D48'),
      swatch(700, '#BE123C'),
      swatch(800, '#9F1239'),
      swatch(900, '#881337'),
      swatch(950, '#4C0519'),
    ],
  },
  {
    id: 'slate',
    name: 'Slate',
    steps: [
      swatch(50, '#F8FAFC'),
      swatch(100, '#F1F5F9'),
      swatch(200, '#E2E8F0'),
      swatch(300, '#CBD5E1'),
      swatch(400, '#94A3B8'),
      swatch(500, '#64748B'),
      swatch(600, '#475569'),
      swatch(700, '#334155'),
      swatch(800, '#1E293B'),
      swatch(900, '#0F172A'),
      swatch(950, '#020617'),
    ],
  },
  {
    id: 'gray',
    name: 'Gray',
    steps: [
      swatch(50, '#F9FAFB'),
      swatch(100, '#F3F4F6'),
      swatch(200, '#E5E7EB'),
      swatch(300, '#D1D5DB'),
      swatch(400, '#9CA3AF'),
      swatch(500, '#6B7280'),
      swatch(600, '#4B5563'),
      swatch(700, '#374151'),
      swatch(800, '#1F2937'),
      swatch(900, '#111827'),
      swatch(950, '#030712'),
    ],
  },
  {
    id: 'zinc',
    name: 'Zinc',
    steps: [
      swatch(50, '#FAFAFA'),
      swatch(100, '#F4F4F5'),
      swatch(200, '#E4E4E7'),
      swatch(300, '#D4D4D8'),
      swatch(400, '#A1A1AA'),
      swatch(500, '#71717A'),
      swatch(600, '#52525B'),
      swatch(700, '#3F3F46'),
      swatch(800, '#27272A'),
      swatch(900, '#18181B'),
      swatch(950, '#09090B'),
    ],
  },
  {
    id: 'neutral',
    name: 'Neutral',
    steps: [
      swatch(50, '#FAFAFA'),
      swatch(100, '#F5F5F5'),
      swatch(200, '#E5E5E5'),
      swatch(300, '#D4D4D4'),
      swatch(400, '#A3A3A3'),
      swatch(500, '#737373'),
      swatch(600, '#525252'),
      swatch(700, '#404040'),
      swatch(800, '#262626'),
      swatch(900, '#171717'),
      swatch(950, '#0A0A0A'),
    ],
  },
  {
    id: 'stone',
    name: 'Stone',
    steps: [
      swatch(50, '#FAFAF9'),
      swatch(100, '#F5F5F4'),
      swatch(200, '#E7E5E4'),
      swatch(300, '#D6D3D1'),
      swatch(400, '#A8A29E'),
      swatch(500, '#78716C'),
      swatch(600, '#57534E'),
      swatch(700, '#44403C'),
      swatch(800, '#292524'),
      swatch(900, '#1C1917'),
      swatch(950, '#0C0A09'),
    ],
  },
];

/** shadcn/ui base color scales (HEX, 11 steps). */
const SHADCN_BASES: PresetFamilyDefinition[] = [
  {
    id: 'neutral',
    name: 'Neutral',
    steps: [
      swatch(50, '#FAFAFA'),
      swatch(100, '#F5F5F5'),
      swatch(200, '#E5E5E5'),
      swatch(300, '#D4D4D4'),
      swatch(400, '#A3A3A3'),
      swatch(500, '#737373'),
      swatch(600, '#525252'),
      swatch(700, '#404040'),
      swatch(800, '#262626'),
      swatch(900, '#171717'),
      swatch(950, '#0A0A0A'),
    ],
  },
  {
    id: 'stone',
    name: 'Stone',
    steps: [
      swatch(50, '#FAFAF9'),
      swatch(100, '#F5F5F4'),
      swatch(200, '#E7E5E4'),
      swatch(300, '#D6D3D1'),
      swatch(400, '#A8A29E'),
      swatch(500, '#78716C'),
      swatch(600, '#57534E'),
      swatch(700, '#44403C'),
      swatch(800, '#292524'),
      swatch(900, '#1C1917'),
      swatch(950, '#0C0A09'),
    ],
  },
  {
    id: 'zinc',
    name: 'Zinc',
    steps: [
      swatch(50, '#FAFAFA'),
      swatch(100, '#F4F4F5'),
      swatch(200, '#E4E4E7'),
      swatch(300, '#D4D4D8'),
      swatch(400, '#A1A1AA'),
      swatch(500, '#71717A'),
      swatch(600, '#52525B'),
      swatch(700, '#3F3F46'),
      swatch(800, '#27272A'),
      swatch(900, '#18181B'),
      swatch(950, '#09090B'),
    ],
  },
  {
    id: 'slate',
    name: 'Slate',
    steps: [
      swatch(50, '#F8FAFC'),
      swatch(100, '#F1F5F9'),
      swatch(200, '#E2E8F0'),
      swatch(300, '#CBD5E1'),
      swatch(400, '#94A3B8'),
      swatch(500, '#64748B'),
      swatch(600, '#475569'),
      swatch(700, '#334155'),
      swatch(800, '#1E293B'),
      swatch(900, '#0F172A'),
      swatch(950, '#020617'),
    ],
  },
  {
    id: 'gray',
    name: 'Gray',
    steps: [
      swatch(50, '#F9FAFB'),
      swatch(100, '#F3F4F6'),
      swatch(200, '#E5E7EB'),
      swatch(300, '#D1D5DB'),
      swatch(400, '#9CA3AF'),
      swatch(500, '#6B7280'),
      swatch(600, '#4B5563'),
      swatch(700, '#374151'),
      swatch(800, '#1F2937'),
      swatch(900, '#111827'),
      swatch(950, '#030712'),
    ],
  },
  {
    id: 'mauve',
    name: 'Mauve',
    steps: [
      swatch(50, '#FAFAFA'),
      swatch(100, '#F4F4F5'),
      swatch(200, '#E4E4E7'),
      swatch(300, '#D4D4D8'),
      swatch(400, '#A1A1AA'),
      swatch(500, '#71717A'),
      swatch(600, '#52525B'),
      swatch(700, '#3F3F46'),
      swatch(800, '#27272A'),
      swatch(900, '#18181B'),
      swatch(950, '#09090B'),
    ],
  },
  {
    id: 'olive',
    name: 'Olive',
    steps: [
      swatch(50, '#FCFDF4'),
      swatch(100, '#F8FBE8'),
      swatch(200, '#F0F5C5'),
      swatch(300, '#E5EB8F'),
      swatch(400, '#CED461'),
      swatch(500, '#AAB12C'),
      swatch(600, '#8A931A'),
      swatch(700, '#6B7411'),
      swatch(800, '#4D540C'),
      swatch(900, '#363C09'),
      swatch(950, '#1F2204'),
    ],
  },
  {
    id: 'mist',
    name: 'Mist',
    steps: [
      swatch(50, '#F8FAFB'),
      swatch(100, '#EEF1F4'),
      swatch(200, '#DBE2E9'),
      swatch(300, '#BCCAD7'),
      swatch(400, '#92AABD'),
      swatch(500, '#6B859A'),
      swatch(600, '#576A7E'),
      swatch(700, '#465361'),
      swatch(800, '#37414C'),
      swatch(900, '#2A323B'),
      swatch(950, '#1C2025'),
    ],
  },
  {
    id: 'taupe',
    name: 'Taupe',
    steps: [
      swatch(50, '#FBFAF8'),
      swatch(100, '#F4F2EC'),
      swatch(200, '#E8E4D9'),
      swatch(300, '#D7CFBC'),
      swatch(400, '#B9A88A'),
      swatch(500, '#9B8663'),
      swatch(600, '#826E50'),
      swatch(700, '#675742'),
      swatch(800, '#524535'),
      swatch(900, '#3F362A'),
      swatch(950, '#28221B'),
    ],
  },
];

/** Bootstrap 5.3 default theme colors (HEX, 9 steps for blue/indigo/etc). */
const BOOTSTRAP: PresetFamilyDefinition[] = [
  {
    id: 'primary',
    name: 'Primary',
    steps: [
      swatch(100, '#CFE2FF'),
      swatch(200, '#9EC5FE'),
      swatch(300, '#6EA8FE'),
      swatch(400, '#3D8BFD'),
      swatch(500, '#0D6EFD'),
      swatch(600, '#0A58CA'),
      swatch(700, '#084298'),
      swatch(800, '#052C65'),
      swatch(900, '#031633'),
    ],
  },
  {
    id: 'secondary',
    name: 'Secondary',
    steps: [
      swatch(100, '#E2E3E5'),
      swatch(200, '#C4C6CB'),
      swatch(300, '#A6AAAE'),
      swatch(400, '#888D90'),
      swatch(500, '#6C757D'),
      swatch(600, '#565E64'),
      swatch(700, '#3F444A'),
      swatch(800, '#292C30'),
      swatch(900, '#1A1D20'),
    ],
  },
  {
    id: 'success',
    name: 'Success',
    steps: [
      swatch(100, '#D1E7DD'),
      swatch(200, '#A3CFBB'),
      swatch(300, '#75B798'),
      swatch(400, '#479F76'),
      swatch(500, '#198754'),
      swatch(600, '#146C43'),
      swatch(700, '#0F5132'),
      swatch(800, '#0A3622'),
      swatch(900, '#051B11'),
    ],
  },
  {
    id: 'danger',
    name: 'Danger',
    steps: [
      swatch(100, '#F8D7DA'),
      swatch(200, '#F1AEB5'),
      swatch(300, '#EA868F'),
      swatch(400, '#E35D6A'),
      swatch(500, '#DC3545'),
      swatch(600, '#B02A37'),
      swatch(700, '#842029'),
      swatch(800, '#58151C'),
      swatch(900, '#2C0B0E'),
    ],
  },
  {
    id: 'warning',
    name: 'Warning',
    steps: [
      swatch(100, '#FFF3CD'),
      swatch(200, '#FFE69C'),
      swatch(300, '#FFDA6A'),
      swatch(400, '#FFCD39'),
      swatch(500, '#FFC107'),
      swatch(600, '#CC9A06'),
      swatch(700, '#997404'),
      swatch(800, '#664D03'),
      swatch(900, '#332701'),
    ],
  },
  {
    id: 'info',
    name: 'Info',
    steps: [
      swatch(100, '#CFF4FC'),
      swatch(200, '#9EEAF9'),
      swatch(300, '#6EDFF6'),
      swatch(400, '#3DD5F3'),
      swatch(500, '#0DCDF0'),
      swatch(600, '#0AA2C0'),
      swatch(700, '#087990'),
      swatch(800, '#055160'),
      swatch(900, '#032830'),
    ],
  },
  {
    id: 'light',
    name: 'Light',
    steps: [
      swatch(100, '#FEFEFE'),
      swatch(200, '#FDFEFE'),
      swatch(300, '#FCFDFD'),
      swatch(400, '#F9FAFB'),
      swatch(500, '#F8F9FA'),
      swatch(600, '#D3D4D5'),
      swatch(700, '#A8AAAB'),
      swatch(800, '#7C7E80'),
      swatch(900, '#4D4F51'),
    ],
  },
  {
    id: 'dark',
    name: 'Dark',
    steps: [
      swatch(100, '#D3D3D4'),
      swatch(200, '#A7A8AA'),
      swatch(300, '#7B7C7F'),
      swatch(400, '#4F5154'),
      swatch(500, '#212529'),
      swatch(600, '#1D2125'),
      swatch(700, '#16191C'),
      swatch(800, '#0F1113'),
      swatch(900, '#08090A'),
    ],
  },
  {
    id: 'blue',
    name: 'Blue',
    steps: [
      swatch(100, '#CFE2FF'),
      swatch(200, '#9EC5FE'),
      swatch(300, '#6EA8FE'),
      swatch(400, '#3D8BFD'),
      swatch(500, '#0D6EFD'),
      swatch(600, '#0A58CA'),
      swatch(700, '#084298'),
      swatch(800, '#052C65'),
      swatch(900, '#031633'),
    ],
  },
  {
    id: 'indigo',
    name: 'Indigo',
    steps: [
      swatch(100, '#E0CFFC'),
      swatch(200, '#C29EFA'),
      swatch(300, '#A370F7'),
      swatch(400, '#8540F5'),
      swatch(500, '#6610F2'),
      swatch(600, '#520DC2'),
      swatch(700, '#3D0A91'),
      swatch(800, '#290659'),
      swatch(900, '#14032C'),
    ],
  },
  {
    id: 'purple',
    name: 'Purple',
    steps: [
      swatch(100, '#D8C7F8'),
      swatch(200, '#B18DF1'),
      swatch(300, '#8A56EA'),
      swatch(400, '#6B43D8'),
      swatch(500, '#6F42C1'),
      swatch(600, '#59359A'),
      swatch(700, '#432874'),
      swatch(800, '#2C1A4D'),
      swatch(900, '#160D27'),
    ],
  },
  {
    id: 'pink',
    name: 'Pink',
    steps: [
      swatch(100, '#F7D6E6'),
      swatch(200, '#EFADCE'),
      swatch(300, '#E685B5'),
      swatch(400, '#DE5C9D'),
      swatch(500, '#D63384'),
      swatch(600, '#AB296A'),
      swatch(700, '#801F4F'),
      swatch(800, '#561435'),
      swatch(900, '#2B0A1A'),
    ],
  },
  {
    id: 'red',
    name: 'Red',
    steps: [
      swatch(100, '#F8D2D2'),
      swatch(200, '#F1A8A8'),
      swatch(300, '#EA7E7E'),
      swatch(400, '#E35555'),
      swatch(500, '#DC3545'),
      swatch(600, '#B02A37'),
      swatch(700, '#842029'),
      swatch(800, '#58151C'),
      swatch(900, '#2C0B0E'),
    ],
  },
  {
    id: 'orange',
    name: 'Orange',
    steps: [
      swatch(100, '#FFE5D0'),
      swatch(200, '#FFCAA0'),
      swatch(300, '#FFAF70'),
      swatch(400, '#FF943F'),
      swatch(500, '#FD7E14'),
      swatch(600, '#CA6510'),
      swatch(700, '#984C0C'),
      swatch(800, '#653208'),
      swatch(900, '#331904'),
    ],
  },
  {
    id: 'yellow',
    name: 'Yellow',
    steps: [
      swatch(100, '#FFF3CD'),
      swatch(200, '#FFE69C'),
      swatch(300, '#FFDA6A'),
      swatch(400, '#FFCD39'),
      swatch(500, '#FFC107'),
      swatch(600, '#CC9A06'),
      swatch(700, '#997404'),
      swatch(800, '#664D03'),
      swatch(900, '#332701'),
    ],
  },
  {
    id: 'green',
    name: 'Green',
    steps: [
      swatch(100, '#D1E7DD'),
      swatch(200, '#A3CFBB'),
      swatch(300, '#75B798'),
      swatch(400, '#479F76'),
      swatch(500, '#198754'),
      swatch(600, '#146C43'),
      swatch(700, '#0F5132'),
      swatch(800, '#0A3622'),
      swatch(900, '#051B11'),
    ],
  },
  {
    id: 'teal',
    name: 'Teal',
    steps: [
      swatch(100, '#D2F4EA'),
      swatch(200, '#A4E7D4'),
      swatch(300, '#76DABE'),
      swatch(400, '#48CDA8'),
      swatch(500, '#20C997'),
      swatch(600, '#1AA179'),
      swatch(700, '#13795B'),
      swatch(800, '#0D503C'),
      swatch(900, '#06281E'),
    ],
  },
  {
    id: 'cyan',
    name: 'Cyan',
    steps: [
      swatch(100, '#CFF4FC'),
      swatch(200, '#9EEAF9'),
      swatch(300, '#6EDFF6'),
      swatch(400, '#3DD5F3'),
      swatch(500, '#0DCDF0'),
      swatch(600, '#0AA2C0'),
      swatch(700, '#087990'),
      swatch(800, '#055160'),
      swatch(900, '#032830'),
    ],
  },
  {
    id: 'gray',
    name: 'Gray',
    steps: [
      swatch(100, '#E9ECEF'),
      swatch(200, '#CFD3D8'),
      swatch(300, '#B5BAC1'),
      swatch(400, '#9AA0A6'),
      swatch(500, '#6C757D'),
      swatch(600, '#565E64'),
      swatch(700, '#3F444A'),
      swatch(800, '#292C30'),
      swatch(900, '#161719'),
    ],
  },
  {
    id: 'black',
    name: 'Black',
    steps: [swatch(500, '#000000')],
  },
  {
    id: 'white',
    name: 'White',
    steps: [swatch(500, '#FFFFFF')],
  },
];

/** Radix Colors v3 – 12-step neutral-free color scales. */
const RADIX: PresetFamilyDefinition[] = [
  {
    id: 'tomato',
    name: 'Tomato',
    steps: [
      swatch(50, '#FFFCFC'),
      swatch(100, '#FFF8F7'),
      swatch(200, '#FEEFE6'),
      swatch(300, '#FCD9CC'),
      swatch(400, '#FAB89B'),
      swatch(500, '#F89070'),
      swatch(600, '#F26C4B'),
      swatch(700, '#E1502E'),
      swatch(800, '#C23817'),
      swatch(900, '#9B260F'),
      swatch(950, '#7B1507'),
      swatch(1000, '#5C0A02'),
    ],
  },
  {
    id: 'red',
    name: 'Red',
    steps: [
      swatch(50, '#FFFCFC'),
      swatch(100, '#FFF7F7'),
      swatch(200, '#FEEAEA'),
      swatch(300, '#FED5D5'),
      swatch(400, '#FCB3B3'),
      swatch(500, '#F88A8A'),
      swatch(600, '#EF5A5A'),
      swatch(700, '#E13838'),
      swatch(800, '#C72020'),
      swatch(900, '#A11414'),
      swatch(950, '#820A0A'),
      swatch(1000, '#5C0303'),
    ],
  },
  {
    id: 'ruby',
    name: 'Ruby',
    steps: [
      swatch(50, '#FFFCFD'),
      swatch(100, '#FFF6F8'),
      swatch(200, '#FDE9EF'),
      swatch(300, '#FBD0DE'),
      swatch(400, '#F7A5C0'),
      swatch(500, '#EF7AAA'),
      swatch(600, '#E54F88'),
      swatch(700, '#D3326C'),
      swatch(800, '#BC1B54'),
      swatch(900, '#9A0E40'),
      swatch(950, '#7A0632'),
      swatch(1000, '#56021F'),
    ],
  },
  {
    id: 'crimson',
    name: 'Crimson',
    steps: [
      swatch(50, '#FFF7F9'),
      swatch(100, '#FFEEF1'),
      swatch(200, '#FFD9E0'),
      swatch(300, '#FFB3C0'),
      swatch(400, '#FB7F94'),
      swatch(500, '#F45271'),
      swatch(600, '#E72D5A'),
      swatch(700, '#D31A4D'),
      swatch(800, '#B81043'),
      swatch(900, '#9B0A39'),
      swatch(950, '#7A042D'),
      swatch(1000, '#56021F'),
    ],
  },
  {
    id: 'pink',
    name: 'Pink',
    steps: [
      swatch(50, '#FFF7FC'),
      swatch(100, '#FEEEF7'),
      swatch(200, '#FED9EB'),
      swatch(300, '#FCB3D8'),
      swatch(400, '#F881BC'),
      swatch(500, '#EF5DA0'),
      swatch(600, '#E3368B'),
      swatch(700, '#D31C75'),
      swatch(800, '#BD1162'),
      swatch(900, '#A0004E'),
      swatch(950, '#800040'),
      swatch(1000, '#560029'),
    ],
  },
  {
    id: 'plum',
    name: 'Plum',
    steps: [
      swatch(50, '#FDF7FD'),
      swatch(100, '#FBEDFB'),
      swatch(200, '#F7D9F5'),
      swatch(300, '#EFB5EA'),
      swatch(400, '#E387DC'),
      swatch(500, '#D25DC9'),
      swatch(600, '#BE3AB1'),
      swatch(700, '#A52295'),
      swatch(800, '#8C1379'),
      swatch(900, '#720B5F'),
      swatch(950, '#570646'),
      swatch(1000, '#3C022D'),
    ],
  },
  {
    id: 'purple',
    name: 'Purple',
    steps: [
      swatch(50, '#FBFAFE'),
      swatch(100, '#F5F2FE'),
      swatch(200, '#EAE4FD'),
      swatch(300, '#D3C6FB'),
      swatch(400, '#B49EF5'),
      swatch(500, '#957AEC'),
      swatch(600, '#7B5BE0'),
      swatch(700, '#6942D0'),
      swatch(800, '#5A2FBA'),
      swatch(900, '#4B239E'),
      swatch(950, '#391A7B'),
      swatch(1000, '#251058'),
    ],
  },
  {
    id: 'violet',
    name: 'Violet',
    steps: [
      swatch(50, '#FBFAFF'),
      swatch(100, '#F4F3FE'),
      swatch(200, '#E6E5FE'),
      swatch(300, '#CFCAFD'),
      swatch(400, '#B1A6FB'),
      swatch(500, '#9583F5'),
      swatch(600, '#7E66EC'),
      swatch(700, '#6E4FDE'),
      swatch(800, '#5F3DC9'),
      swatch(900, '#502DAF'),
      swatch(950, '#3F2089'),
      swatch(1000, '#2A1363'),
    ],
  },
  {
    id: 'iris',
    name: 'Iris',
    steps: [
      swatch(50, '#FBFBFF'),
      swatch(100, '#F4F4FF'),
      swatch(200, '#E5E5FF'),
      swatch(300, '#CDCDFF'),
      swatch(400, '#ABABFF'),
      swatch(500, '#9090FF'),
      swatch(600, '#7575F4'),
      swatch(700, '#5A5AE0'),
      swatch(800, '#4747C5'),
      swatch(900, '#3838A4'),
      swatch(950, '#2A2A82'),
      swatch(1000, '#1B1B5C'),
    ],
  },
  {
    id: 'indigo',
    name: 'Indigo',
    steps: [
      swatch(50, '#F9FBFF'),
      swatch(100, '#F0F4FF'),
      swatch(200, '#DFE8FF'),
      swatch(300, '#B6CCFF'),
      swatch(400, '#88ADFF'),
      swatch(500, '#5C8EFF'),
      swatch(600, '#3A6EF5'),
      swatch(700, '#2655DD'),
      swatch(800, '#1C45BD'),
      swatch(900, '#163A9C'),
      swatch(950, '#112B7A'),
      swatch(1000, '#0A1A56'),
    ],
  },
  {
    id: 'blue',
    name: 'Blue',
    steps: [
      swatch(50, '#FBFCFF'),
      swatch(100, '#F4F8FF'),
      swatch(200, '#E5EEFF'),
      swatch(300, '#C2D7FF'),
      swatch(400, '#98B8FF'),
      swatch(500, '#739AFF'),
      swatch(600, '#547CE5'),
      swatch(700, '#3A60CC'),
      swatch(800, '#2A4DA8'),
      swatch(900, '#1F3F87'),
      swatch(950, '#172F66'),
      swatch(1000, '#0F1E47'),
    ],
  },
  {
    id: 'cyan',
    name: 'Cyan',
    steps: [
      swatch(50, '#F8FCFD'),
      swatch(100, '#EAF7FA'),
      swatch(200, '#CFEEF2'),
      swatch(300, '#A6DEE7'),
      swatch(400, '#71C7D7'),
      swatch(500, '#46AEBF'),
      swatch(600, '#2D93A5'),
      swatch(700, '#20788A'),
      swatch(800, '#1A6273'),
      swatch(900, '#164F5D'),
      swatch(950, '#0F3A47'),
      swatch(1000, '#0A2632'),
    ],
  },
  {
    id: 'teal',
    name: 'Teal',
    steps: [
      swatch(50, '#F8FCFC'),
      swatch(100, '#EAF7F4'),
      swatch(200, '#CCEFE3'),
      swatch(300, '#A0DDC9'),
      swatch(400, '#65C5AB'),
      swatch(500, '#3EAB91'),
      swatch(600, '#29907A'),
      swatch(700, '#1E7564'),
      swatch(800, '#175E51'),
      swatch(900, '#134A41'),
      swatch(950, '#0D3630'),
      swatch(1000, '#08231F'),
    ],
  },
  {
    id: 'jade',
    name: 'Jade',
    steps: [
      swatch(50, '#F8FCF8'),
      swatch(100, '#E8F6EC'),
      swatch(200, '#C7EBD0'),
      swatch(300, '#9BD9AC'),
      swatch(400, '#6BC188'),
      swatch(500, '#45A569'),
      swatch(600, '#2F8A55'),
      swatch(700, '#226F44'),
      swatch(800, '#1A5836'),
      swatch(900, '#13452A'),
      swatch(950, '#0D3220'),
      swatch(1000, '#082014'),
    ],
  },
  {
    id: 'green',
    name: 'Green',
    steps: [
      swatch(50, '#F8FCF5'),
      swatch(100, '#E8F5E5'),
      swatch(200, '#CFEAC9'),
      swatch(300, '#A6D89E'),
      swatch(400, '#74BF6A'),
      swatch(500, '#48A445'),
      swatch(600, '#348A33'),
      swatch(700, '#266F26'),
      swatch(800, '#1B581B'),
      swatch(900, '#134513'),
      swatch(950, '#0C320C'),
      swatch(1000, '#062007'),
    ],
  },
  {
    id: 'grass',
    name: 'Grass',
    steps: [
      swatch(50, '#F9FCF4'),
      swatch(100, '#ECF6E0'),
      swatch(200, '#D5EAB9'),
      swatch(300, '#B4D886'),
      swatch(400, '#8FC158'),
      swatch(500, '#6BA63B'),
      swatch(600, '#55892C'),
      swatch(700, '#436C20'),
      swatch(800, '#345318'),
      swatch(900, '#283F12'),
      swatch(950, '#1B2C0B'),
      swatch(1000, '#0F1A05'),
    ],
  },
  {
    id: 'brown',
    name: 'Brown',
    steps: [
      swatch(50, '#FBF9F6'),
      swatch(100, '#F4EFE7'),
      swatch(200, '#E7DDD0'),
      swatch(300, '#D2BFA8'),
      swatch(400, '#B89A78'),
      swatch(500, '#9C7B55'),
      swatch(600, '#82603F'),
      swatch(700, '#684A2E'),
      swatch(800, '#523822'),
      swatch(900, '#402919'),
      swatch(950, '#2D1C10'),
      swatch(1000, '#1A0F07'),
    ],
  },
  {
    id: 'orange',
    name: 'Orange',
    steps: [
      swatch(50, '#FEFAF5'),
      swatch(100, '#FCF1E5'),
      swatch(200, '#F8DFC6'),
      swatch(300, '#F0C29A'),
      swatch(400, '#E69F65'),
      swatch(500, '#D9803C'),
      swatch(600, '#C56428'),
      swatch(700, '#AC4E1B'),
      swatch(800, '#8D3D14'),
      swatch(900, '#6E2D0E'),
      swatch(950, '#501E08'),
      swatch(1000, '#330F03'),
    ],
  },
  {
    id: 'amber',
    name: 'Amber',
    steps: [
      swatch(50, '#FEFAEF'),
      swatch(100, '#FCEFD7'),
      swatch(200, '#F8DDA7'),
      swatch(300, '#F0C26C'),
      swatch(400, '#E5A332'),
      swatch(500, '#D38511'),
      swatch(600, '#B66808'),
      swatch(700, '#965005'),
      swatch(800, '#783D04'),
      swatch(900, '#5C2E02'),
      swatch(950, '#421F00'),
      swatch(1000, '#280F00'),
    ],
  },
  {
    id: 'yellow',
    name: 'Yellow',
    steps: [
      swatch(50, '#FEFCEC'),
      swatch(100, '#FEF7C5'),
      swatch(200, '#FDEC8C'),
      swatch(300, '#FBD94F'),
      swatch(400, '#F5C226'),
      swatch(500, '#EBA60B'),
      swatch(600, '#CC8406'),
      swatch(700, '#A36503'),
      swatch(800, '#7C4B02'),
      swatch(900, '#5A3601'),
      swatch(950, '#3D2300'),
      swatch(1000, '#1F1100'),
    ],
  },
  {
    id: 'lime',
    name: 'Lime',
    steps: [
      swatch(50, '#FBFCE8'),
      swatch(100, '#F4F7C2'),
      swatch(200, '#E6EE85'),
      swatch(300, '#D2E24A'),
      swatch(400, '#BBD128'),
      swatch(500, '#A1B912'),
      swatch(600, '#86990B'),
      swatch(700, '#6B7908'),
      swatch(800, '#525E06'),
      swatch(900, '#3D4804'),
      swatch(950, '#2A3302'),
      swatch(1000, '#181E00'),
    ],
  },
  {
    id: 'mint',
    name: 'Mint',
    steps: [
      swatch(50, '#F4FCF3'),
      swatch(100, '#E0F5E0'),
      swatch(200, '#BFE9C3'),
      swatch(300, '#8CD99B'),
      swatch(400, '#5CC275'),
      swatch(500, '#3CA85A'),
      swatch(600, '#2A8C48'),
      swatch(700, '#1F6F39'),
      swatch(800, '#17572C'),
      swatch(900, '#114321'),
      swatch(950, '#0A3017'),
      swatch(1000, '#051D0D'),
    ],
  },
  {
    id: 'sky',
    name: 'Sky',
    steps: [
      swatch(50, '#F7FBFE'),
      swatch(100, '#E6F4FC'),
      swatch(200, '#C7E6F8'),
      swatch(300, '#9DD2F1'),
      swatch(400, '#6CB9E7'),
      swatch(500, '#4A9FDA'),
      swatch(600, '#3283BC'),
      swatch(700, '#236999'),
      swatch(800, '#1A5478'),
      swatch(900, '#13415D'),
      swatch(950, '#0C2E45'),
      swatch(1000, '#061C2D'),
    ],
  },
  {
    id: 'gray',
    name: 'Gray',
    steps: [
      swatch(50, '#FCFCFD'),
      swatch(100, '#F8F8F9'),
      swatch(200, '#EEEEEF'),
      swatch(300, '#DEDEE0'),
      swatch(400, '#C0C0C3'),
      swatch(500, '#9B9BA0'),
      swatch(600, '#7B7B80'),
      swatch(700, '#5D5D62'),
      swatch(800, '#3F3F44'),
      swatch(900, '#252528'),
      swatch(950, '#131316'),
      swatch(1000, '#060608'),
    ],
  },
  {
    id: 'mauve',
    name: 'Mauve',
    steps: [
      swatch(50, '#FCFCFD'),
      swatch(100, '#F8F8F9'),
      swatch(200, '#EEEEF0'),
      swatch(300, '#DFDFE2'),
      swatch(400, '#C2C2C6'),
      swatch(500, '#9D9DA3'),
      swatch(600, '#7D7D84'),
      swatch(700, '#606067'),
      swatch(800, '#42424A'),
      swatch(900, '#27272E'),
      swatch(950, '#181820'),
      swatch(1000, '#0A0A12'),
    ],
  },
  {
    id: 'slate',
    name: 'Slate',
    steps: [
      swatch(50, '#FBFCFD'),
      swatch(100, '#F7F8FA'),
      swatch(200, '#ECEEF2'),
      swatch(300, '#D8DCE3'),
      swatch(400, '#B6BCC8'),
      swatch(500, '#8C95A6'),
      swatch(600, '#6B7585'),
      swatch(700, '#525A6A'),
      swatch(800, '#3A4150'),
      swatch(900, '#232933'),
      swatch(950, '#15181F'),
      swatch(1000, '#08090D'),
    ],
  },
  {
    id: 'sage',
    name: 'Sage',
    steps: [
      swatch(50, '#FBFCFC'),
      swatch(100, '#F7F9F8'),
      swatch(200, '#ECEFED'),
      swatch(300, '#D7DDD8'),
      swatch(400, '#B2BAB5'),
      swatch(500, '#87938C'),
      swatch(600, '#67736D'),
      swatch(700, '#505A55'),
      swatch(800, '#38413D'),
      swatch(900, '#22282A'),
      swatch(950, '#141819'),
      swatch(1000, '#070A0A'),
    ],
  },
  {
    id: 'olive',
    name: 'Olive',
    steps: [
      swatch(50, '#FBFCFB'),
      swatch(100, '#F7F8F4'),
      swatch(200, '#ECEFE5'),
      swatch(300, '#D7DCCB'),
      swatch(400, '#B4BAA0'),
      swatch(500, '#89937A'),
      swatch(600, '#69735D'),
      swatch(700, '#525B49'),
      swatch(800, '#394234'),
      swatch(900, '#232924'),
      swatch(950, '#151915'),
      swatch(1000, '#070807'),
    ],
  },
  {
    id: 'sand',
    name: 'Sand',
    steps: [
      swatch(50, '#FBFBF9'),
      swatch(100, '#F7F6F1'),
      swatch(200, '#EBE9E0'),
      swatch(300, '#D5D2C2'),
      swatch(400, '#B0AC95'),
      swatch(500, '#878470'),
      swatch(600, '#696656'),
      swatch(700, '#535043'),
      swatch(800, '#3B392F'),
      swatch(900, '#25241C'),
      swatch(950, '#161510'),
      swatch(1000, '#080804'),
    ],
  },
];

export const OFFICIAL_PRESETS: Record<string, PresetDefinition> = {
  tailwind: {
    name: 'Tailwind v4',
    description: 'The default color palette shipped with Tailwind CSS v4 (OKLCH, 11 steps).',
    families: TAILWIND,
  },
  'shadcn-bases': {
    name: 'shadcn Bases',
    description:
      'shadcn/ui base color scales (neutral, stone, zinc, slate, gray, mauve, olive, mist, taupe).',
    families: SHADCN_BASES,
  },
  bootstrap: {
    name: 'Bootstrap 5.3',
    description: 'Bootstrap 5.3 default theme and palette colors (9 steps + black/white).',
    families: BOOTSTRAP,
  },
  radix: {
    name: 'Radix Colors',
    description: 'Radix Colors v3 – 12-step neutral-free color scales.',
    families: RADIX,
  },
};
